import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, FunnelIcon, ChevronDownIcon, ChevronUpIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';
import { GoatKiddingLog, TypeOfBirth } from '../../types/api';

type SortField = 'kidding_date' | 'doe_tag' | 'kids_born';
type SortDirection = 'asc' | 'desc';

const GoatKiddingLogsPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [allLogs, setAllLogs] = useState<GoatKiddingLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const getDefaultDates = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const startOfYear = new Date(currentYear, 0, 1); // January 1
    const endOfYear = new Date(currentYear, 11, 31); // December 31
    return {
      startDate: startOfYear.toISOString().split('T')[0],
      endDate: endOfYear.toISOString().split('T')[0]
    };
  };
  
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [searchDoeTag, setSearchDoeTag] = useState('');
  const [filterKidsBorn, setFilterKidsBorn] = useState<string>('');
  const [filterTOB, setFilterTOB] = useState<TypeOfBirth | ''>('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Sorting
  const [sortField, setSortField] = useState<SortField>('kidding_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadLogs();
    }
  }, [selectedLocation?.id, businessType, startDate, endDate]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ location_id: selectedLocation?.id || '' });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await apiClient.get(`/goat/kidding-logs?${params.toString()}`);
      setAllLogs(response.data || []);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load kidding logs'));
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort logs
  const filteredAndSortedLogs = useMemo(() => {
    let filtered = [...allLogs];
    
    // Filter by doe tag search
    if (searchDoeTag.trim()) {
      filtered = filtered.filter(log => 
        log.doe_tag.toLowerCase().includes(searchDoeTag.toLowerCase().trim())
      );
    }
    
    // Filter by kids born
    if (filterKidsBorn) {
      const kidsCount = parseInt(filterKidsBorn);
      filtered = filtered.filter(log => log.kids_born === kidsCount);
    }
    
    // Filter by type of birth
    if (filterTOB) {
      filtered = filtered.filter(log => log.tob === filterTOB);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortField) {
        case 'kidding_date':
          aValue = new Date(a.kidding_date).getTime();
          bValue = new Date(b.kidding_date).getTime();
          break;
        case 'doe_tag':
          aValue = a.doe_tag.toLowerCase();
          bValue = b.doe_tag.toLowerCase();
          break;
        case 'kids_born':
          aValue = a.kids_born;
          bValue = b.kids_born;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [allLogs, searchDoeTag, filterKidsBorn, filterTOB, sortField, sortDirection]);

  // Summary statistics
  const stats = useMemo(() => {
    const totalKids = filteredAndSortedLogs.reduce((sum, log) => sum + log.kids_born, 0);
    const totalEvents = filteredAndSortedLogs.length;
    const avgKids = totalEvents > 0 ? (totalKids / totalEvents).toFixed(2) : '0.00';
    
    return {
      totalKids,
      totalEvents,
      avgKids
    };
  }, [filteredAndSortedLogs]);

  const clearFilters = () => {
    const defaultDates = getDefaultDates();
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
    setSearchDoeTag('');
    setFilterKidsBorn('');
    setFilterTOB('');
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTOBBadgeColor = (tob?: TypeOfBirth) => {
    switch (tob) {
      case 'single':
        return 'bg-blue-100 text-blue-800';
      case 'double':
        return 'bg-green-100 text-green-800';
      case 'triplet':
        return 'bg-yellow-100 text-yellow-800';
      case 'four':
        return 'bg-orange-100 text-orange-800';
      case 'five':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTOBLabel = (tob?: TypeOfBirth) => {
    if (!tob) return '-';
    return tob.charAt(0).toUpperCase() + tob.slice(1);
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/goat/logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🐐 Kidding Logs</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
        <Link
          to="/goat/kidding/new"
          className="btn-touch bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Record Kidding</span>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full flex items-center justify-between mb-4"
        >
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-500" />
            <span className="font-medium text-gray-700">Filters</span>
            {(searchDoeTag || filterKidsBorn || filterTOB || startDate !== defaultDates.startDate || endDate !== defaultDates.endDate) && (
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Active</span>
            )}
          </div>
          {showFilters ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          )}
        </button>

        {showFilters && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label">From Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">To Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Search Doe Tag</label>
                <input
                  type="text"
                  value={searchDoeTag}
                  onChange={(e) => setSearchDoeTag(e.target.value)}
                  placeholder="Enter doe tag..."
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Kids Born</label>
                <select
                  value={filterKidsBorn}
                  onChange={(e) => setFilterKidsBorn(e.target.value)}
                  className="form-input"
                >
                  <option value="">All</option>
                  <option value="1">1 Kid</option>
                  <option value="2">2 Kids</option>
                  <option value="3">3 Kids</option>
                  <option value="4">4 Kids</option>
                  <option value="5">5 Kids</option>
                </select>
              </div>
              <div>
                <label className="form-label">Type of Birth</label>
                <select
                  value={filterTOB}
                  onChange={(e) => setFilterTOB(e.target.value as TypeOfBirth | '')}
                  className="form-input"
                >
                  <option value="">All</option>
                  <option value="single">Single</option>
                  <option value="double">Double</option>
                  <option value="triplet">Triplet</option>
                  <option value="four">Four</option>
                  <option value="five">Five</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={clearFilters}
                className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                Reset Filters
              </button>
              <button
                onClick={loadLogs}
                className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center justify-center space-x-2"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Statistics */}
      {filteredAndSortedLogs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card p-4 bg-blue-50">
            <div className="text-xs text-blue-600 font-medium mb-1">Total Kidding Events</div>
            <div className="text-2xl font-bold text-blue-700">{stats.totalEvents}</div>
            {allLogs.length !== filteredAndSortedLogs.length && (
              <div className="text-xs text-blue-600 mt-1">
                (Filtered from {allLogs.length} total)
              </div>
            )}
          </div>
          <div className="card p-4 bg-green-50">
            <div className="text-xs text-green-600 font-medium mb-1">Total Kids Born</div>
            <div className="text-2xl font-bold text-green-700">{stats.totalKids}</div>
            <div className="text-xs text-green-600 mt-1">
              In selected period
            </div>
          </div>
          <div className="card p-4 bg-purple-50">
            <div className="text-xs text-purple-600 font-medium mb-1">Average Kids/Kidding</div>
            <div className="text-2xl font-bold text-purple-700">{stats.avgKids}</div>
            <div className="text-xs text-purple-600 mt-1">
              Per kidding event
            </div>
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="md:hidden">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Kidding Records ({filteredAndSortedLogs.length})
          </h2>

          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : filteredAndSortedLogs.length > 0 ? (
            <div className="space-y-3">
              {filteredAndSortedLogs.map((log) => (
                <div key={log.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="font-semibold text-gray-900">Doe: {log.doe_tag}</span>
                        {log.tob && (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTOBBadgeColor(log.tob)}`}>
                            {getTOBLabel(log.tob)}
                          </span>
                        )}
                      </div>
                      <div className="text-green-600 font-medium mb-2">
                        {log.kids_born} {log.kids_born === 1 ? 'kid' : 'kids'} born
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>Date: {formatDate(log.kidding_date)}</div>
                        {log.kid_tags && log.kid_tags.length > 0 && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">Kid Tags: </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {log.kid_tags.map((tag, idx) => (
                                <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {log.breeding_log_id && (
                          <div className="mt-2">
                            <Link
                              to={`/goat/breeding-logs`}
                              className="text-blue-600 hover:text-blue-800 text-xs"
                            >
                              View Breeding Log →
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🐐</div>
              <p className="text-gray-500 mb-2">No kidding records found</p>
              <p className="text-sm text-gray-400 mb-4">
                {allLogs.length > 0 
                  ? 'Try adjusting your filters' 
                  : 'Start recording kidding events'}
              </p>
              {allLogs.length === 0 && (
                <Link
                  to="/goat/kidding/new"
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Record First Kidding
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Kidding Records ({filteredAndSortedLogs.length})
          </h2>

          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : filteredAndSortedLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('kidding_date')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Date</span>
                        {sortField === 'kidding_date' && (
                          <span className="text-blue-600">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('doe_tag')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Doe Tag</span>
                        {sortField === 'doe_tag' && (
                          <span className="text-blue-600">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('kids_born')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Kids Born</span>
                        {sortField === 'kids_born' && (
                          <span className="text-blue-600">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type of Birth
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kid Tags
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Breeding Log
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(log.kidding_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {log.doe_tag}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 font-semibold">
                        {log.kids_born}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.tob ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTOBBadgeColor(log.tob)}`}>
                            {getTOBLabel(log.tob)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.kid_tags && log.kid_tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {log.kid_tags.map((tag, idx) => (
                              <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.breeding_log_id ? (
                          <Link
                            to={`/goat/breeding-logs`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            View →
                          </Link>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🐐</div>
              <p className="text-gray-500 mb-2">No kidding records found</p>
              <p className="text-sm text-gray-400 mb-4">
                {allLogs.length > 0 
                  ? 'Try adjusting your filters' 
                  : 'Start recording kidding events'}
              </p>
              {allLogs.length === 0 && (
                <Link
                  to="/goat/kidding/new"
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Record First Kidding
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoatKiddingLogsPage;
